import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ErrorNotice,
  Field,
  Modal,
  Select,
  SettingsGroup,
  SettingsSection,
} from '@kiipu/ui';
import { request, safeHttpUrl } from '../api';
import { useSession, useToast } from '../context';
import { isDesktop } from '../lib/client-platform';
import { ProviderLogo } from './provider-logos';

type Connection = { id: string; provider: string; account: { label: string }; status: string };
type Resource = { id: string; name: string; type: string };
type Operation = {
  id: string;
  action: string;
  status: string;
  errorCode?: string;
  result?: { url?: string };
  createdAt: number;
};
type Capabilities = { connections: string[]; identity: string[] };
const label = (provider: string) =>
  ({ notion: 'Notion', raindrop: 'Raindrop', google: 'Google', github: 'GitHub' })[provider] ??
  provider;
const statuses: Record<string, string> = {
  pending: '等待保存',
  running: '正在保存',
  succeeded: '已保存',
  failed: '保存失败',
  blocked: '请重新连接',
  outcome_unknown: '结果尚未确认，请先检查目标应用',
  cancelled: '已取消',
};
function useConnections() {
  const owner = useSession().data?.user?.id;
  return useQuery({
    queryKey: ['integrations', 'connections', owner],
    queryFn: () => request<{ items: Connection[] }>('/integrations/connections'),
    enabled: Boolean(owner) && !isDesktop(),
  });
}
export function IntegrationSettings() {
  const owner = useSession().data?.user?.id;
  const connections = useConnections();
  const capabilities = useQuery({
    queryKey: ['integration-capabilities'],
    queryFn: () => request<Capabilities>('/integrations/capabilities'),
    enabled: !isDesktop(),
  });
  const operations = useQuery({
    queryKey: ['integrations', 'operations', owner],
    queryFn: () => request<{ items: Operation[] }>('/integrations/operations'),
    enabled: Boolean(owner) && !isDesktop(),
    refetchInterval: (q) =>
      q.state.data?.items.some((op) => ['pending', 'running'].includes(op.status)) ? 3000 : false,
  });
  const client = useQueryClient();
  const toast = useToast();
  const connect = useMutation({
    mutationFn: ({ provider, connectionId }: { provider: string; connectionId?: string }) =>
      request<{ authorizationUrl: string }>(`/integrations/oauth/${provider}/start`, {
        method: 'POST',
        body: JSON.stringify({ connectionId }),
      }),
    onSuccess: (flow) => {
      const url = safeHttpUrl(flow.authorizationUrl);
      if (url) window.location.assign(url);
    },
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => request(`/integrations/connections/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['integrations'] });
      toast('已断开应用连接');
    },
  });
  if (isDesktop()) return <p>请在 Kiipu 网页的“应用集成”中管理连接和保存内容。</p>;
  return (
    <>
      <SettingsSection title="已连接的应用" className="settings-section">
        <SettingsGroup>
          {capabilities.data?.connections.length === 0 ? <p>当前尚未启用第三方应用集成。</p> : null}
          {capabilities.data?.connections.map((provider) => (
            <div className="settings-row" key={provider}>
              <div>
                <strong>{label(provider)}</strong>
                <p>授权后，可在阅读器中选择目标并保存条目。</p>
              </div>
              <Button busy={connect.isPending} onClick={() => connect.mutate({ provider })}>
                连接账户
              </Button>
            </div>
          ))}
          {connections.data?.items
            .filter((entry) => entry.status !== 'revoked')
            .map((entry) => (
              <div className="settings-row" key={entry.id}>
                <div>
                  <strong>
                    {label(entry.provider)} · {entry.account.label}
                  </strong>
                  <p>{entry.status === 'active' ? '已连接' : '需要重新授权'}</p>
                </div>
                <div className="row">
                  {entry.status !== 'active' ? (
                    <Button
                      disabled={connect.isPending}
                      onClick={() =>
                        connect.mutate({ provider: entry.provider, connectionId: entry.id })
                      }
                    >
                      重新连接
                    </Button>
                  ) : null}
                  <Button
                    disabled={disconnect.isPending}
                    onClick={() => disconnect.mutate(entry.id)}
                  >
                    断开连接
                  </Button>
                </div>
              </div>
            ))}
        </SettingsGroup>
      </SettingsSection>
      <ErrorNotice
        error={capabilities.error || connections.error || connect.error || disconnect.error}
      />
      <SettingsSection title="最近的保存任务" className="settings-section">
        <SettingsGroup>
          {operations.data?.items.length === 0 ? (
            <p>打开内容库中的条目，选择“保存到应用”开始。</p>
          ) : null}
          {operations.data?.items.slice(0, 20).map((operation) => (
            <div className="settings-row" key={operation.id}>
              <span>{operation.action === 'create_page' ? 'Notion 页面' : 'Raindrop 书签'}</span>
              <span>{statuses[operation.status] ?? operation.status}</span>
            </div>
          ))}
          <ErrorNotice error={operations.error} />
        </SettingsGroup>
      </SettingsSection>
    </>
  );
}

export function IntegrationSaveDialog({
  itemId,
  open,
  onOpenChange,
}: {
  itemId: string;
  open: boolean;
  onOpenChange(open: boolean): void;
}) {
  const owner = useSession().data?.user?.id;
  const connections = useConnections();
  const [connectionId, setConnectionId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [operationId, setOperationId] = useState<string>();
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const active = connections.data?.items.filter((entry) => entry.status === 'active') ?? [];
  const resources = useInfiniteQuery({
    queryKey: ['integrations', 'resources', owner, connectionId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request<{ items: Resource[]; nextCursor?: string }>(
        `/integrations/connections/${connectionId}/resources${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled: open && Boolean(connectionId),
  });
  const save = useMutation({
    mutationFn: () =>
      request<Operation>('/integrations/operations', {
        method: 'POST',
        body: JSON.stringify({
          connectionId,
          itemId,
          resourceId,
          resourceType: resources.data?.pages
            .flatMap((page) => page.items)
            .find((entry) => entry.id === resourceId)?.type,
          idempotencyKey,
        }),
      }),
    onSuccess: (operation) => setOperationId(operation.id),
  });
  const operation = useQuery({
    queryKey: ['integrations', 'operation', owner, operationId],
    queryFn: () => request<Operation>(`/integrations/operations/${operationId}`),
    enabled: open && Boolean(operationId),
    refetchInterval: (query) =>
      query.state.data && ['pending', 'running'].includes(query.state.data.status) ? 2000 : false,
  });
  const status = operation.data ?? save.data;
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="保存到应用">
      <div className="stack">
        {active.length ? (
          <>
            <Field label="应用账户">
              <Select
                value={connectionId}
                onChange={(event) => {
                  setConnectionId(event.target.value);
                  setResourceId('');
                  setOperationId(undefined);
                  save.reset();
                  setIdempotencyKey(crypto.randomUUID());
                }}
                disabled={save.isPending || Boolean(operationId)}
              >
                <option value="">选择已连接的账户</option>
                {active.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {label(entry.provider)} · {entry.account.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="保存位置">
              <Select
                value={resourceId}
                onChange={(event) => {
                  setResourceId(event.target.value);
                  setIdempotencyKey(crypto.randomUUID());
                  save.reset();
                }}
                disabled={
                  !connectionId || resources.isLoading || save.isPending || Boolean(operationId)
                }
              >
                <option value="">
                  {resources.isLoading ? '正在读取可用位置…' : '选择页面或收藏集'}
                </option>
                {resources.data?.pages
                  .flatMap((page) => page.items)
                  .map((entry) => (
                    <option key={`${entry.type}:${entry.id}`} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
              </Select>
            </Field>
            {resources.hasNextPage ? (
              <Button
                disabled={resources.isFetchingNextPage}
                onClick={() => {
                  void resources.fetchNextPage();
                }}
              >
                加载更多位置
              </Button>
            ) : null}
            {connectionId &&
            !resources.isLoading &&
            resources.data?.pages.every((page) => page.items.length === 0) ? (
              <p>没有可用位置。请确认已向应用授权相应的页面或收藏集。</p>
            ) : null}
            <p>Notion 保存条目正文与备注；Raindrop 保存原文链接、摘要、标签与备注。</p>
            {status ? <p role="status">{statuses[status.status] ?? status.status}</p> : null}
            {status?.result?.url && safeHttpUrl(status.result.url) ? (
              <a href={safeHttpUrl(status.result.url)} target="_blank" rel="noopener noreferrer">
                打开保存结果
              </a>
            ) : null}
            <ErrorNotice error={resources.error || save.error || operation.error} />
            <Button
              variant="primary"
              busy={save.isPending}
              disabled={!connectionId || !resourceId || Boolean(operationId)}
              onClick={() => save.mutate()}
            >
              保存条目
            </Button>
          </>
        ) : (
          <p>请先前往设置中的“应用集成”连接 Notion 或 Raindrop。</p>
        )}
        <ErrorNotice error={connections.error} />
      </div>
    </Modal>
  );
}

export function SocialSignInButtons({ divider = false }: { divider?: boolean }) {
  const capabilities = useQuery({
    queryKey: ['integration-capabilities'],
    queryFn: () => request<Capabilities>('/integrations/capabilities'),
    enabled: !isDesktop(),
  });
  const start = useMutation({
    mutationFn: (provider: string) =>
      request<{ authorizationUrl: string }>(`/auth/social/${provider}/start`, {
        method: 'POST',
        body: JSON.stringify(
          (() => {
            try {
              const returnTo = sessionStorage.getItem('kiipu.auth.resume');
              return returnTo && /^\/authorize\?flowId=[0-9a-f-]{36}$/i.test(returnTo)
                ? { returnTo }
                : {};
            } catch {
              return {};
            }
          })(),
        ),
      }),
    onSuccess: (flow) => {
      const url = safeHttpUrl(flow.authorizationUrl);
      if (url) window.location.assign(url);
    },
  });
  if (isDesktop() || !capabilities.data?.identity.length) return null;
  // Keep the chosen provider spinning while the browser leaves for its consent screen.
  const leaving = start.isPending || start.isSuccess;
  return (
    <div className="social-sign-in">
      {divider ? (
        <div className="signin-divider" role="separator">
          <span>或</span>
        </div>
      ) : null}
      <div className="signin-social-list">
        {capabilities.data.identity.map((provider) => {
          const busy = leaving && start.variables === provider;
          return (
            <Button
              key={provider}
              size="lg"
              className="signin-social-button"
              busy={busy}
              disabled={leaving}
              onClick={() => start.mutate(provider)}
            >
              {busy ? null : <ProviderLogo provider={provider} />}
              使用 {label(provider)} 继续
            </Button>
          );
        })}
      </div>
      <ErrorNotice error={start.error} />
    </div>
  );
}
